<template>
  <div class="main-app" v-if="!isLanguageLoading">
    <template v-if="!isAuthorizationPage">
      <router-view :key="$route.path" />
    </template>
    <template v-else>
      <div class="left-app">
        <main-nav-bar />
        <Transition name="fade">
          <button v-if="!store.state.isNavOpen" @click="store.commit('isNav')" class="side-bar-out">
            <svg viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 13L7 7L1 1" stroke="#7A70DF" stroke-width="2" />
            </svg>
          </button>
        </Transition>
      </div>
      <div class="right-app">
        <div class="top-right">
          <NotificationBanner />
          <the-header />
          <main>
            <router-view />
          </main>
        </div>
        <div class="bottom-right">
          <main-footer />
        </div>
      </div>
      <app-dialog />
    </template>
  </div>
  <PreloaderBase v-else position="center" />
  <!--<sign-in v-else/>-->
</template>

<script setup>
// core
import {ref, computed, onUnmounted, watch, getCurrentInstance} from "vue";
import { withProps, createToast, clearToasts } from "mosha-vue-toastify";

import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { showToast } from "./helpers/showToast";

// components
import MainNavBar from "@/components/NavBar/MainNavBar.vue";
import ToastContent from "@/components/Recurring/ToastContent.vue";
import AppDialog from "@/components/Dialog/AppDialog.vue";
import TheHeader from "@/components/Header/TheHeader.vue";
import MainFooter from "@/components/Footer/MainFooter.vue";
import PreloaderBase from "@/components/Recurring/PreloaderBase.vue";
import NotificationBanner from "@/components/Notifications/NotificationBanner.vue";

// data
import { TRANSLATE_LANGUAGES } from "@/data/countriesLanguageList.js";

// composables
import { usePageMeta } from "@/composables/usePageMeta.js";

// ---
const route = useRoute();
const store = useStore();
const { proxy } = getCurrentInstance();
const { t: $t, locale, fallbackLocale } = useI18n();
usePageMeta();

const isLanguageLoading = ref(false);

const code = computed(() => {
  const code = sessionStorage.getItem("code-country");
  return code;
});

const isAuthorizationPage = computed(() => {
  return store?.getters["auth/isLoggedIn"] && route?.name !== "SignIn";
});

watch(
  () => store.getters["auth/getLanguage"],
  () => {
    locale.value = store.getters["auth/getLanguage"];
    document.documentElement.lang = locale.value;
    clearToasts();
  },
  {
    immediate: true,
  }
);

watch(
  () => route?.name,
  () => {
    clearToasts();
  }
);

watch(
  () => store.state.toastContent,
  () => {
    const { status, description } = store.state.toastContent;
    showToast(
      proxy.$moshaToast,
      status.toLowerCase(),
      $t(description),
      {
        hideProgressBar: true
      }
    );
  },
  {
    // deep: true,
  }
);
const convertCodeCountryToLanguage = (codeCountry) => {
  const existingLanguage = TRANSLATE_LANGUAGES.includes(
    codeCountry?.toLowerCase()
  )
    ? codeCountry?.toLowerCase()
    : "en";
  return existingLanguage;
};

const loadDefineLanguage = async () => {
  const languageCookie = await store.dispatch("auth/getLanguageFromCookie");
  if (languageCookie) {
    store.commit("auth/SET_LANGUAGE", languageCookie);
    !sessionStorage.hasOwnProperty("code-country") &&
      (await store.dispatch("auth/getCodeCountry"));
  } else {
    // когда в сессии пусто(не записан код страны)
    if (!sessionStorage.hasOwnProperty("code-country")) {
      const code = await store.dispatch("auth/getCodeCountry");
      store.commit("auth/SET_LANGUAGE", convertCodeCountryToLanguage(code));
    } else {
      // язык берем с сессии кода страны
      store.commit(
        "auth/SET_LANGUAGE",
        convertCodeCountryToLanguage(code.value)
      );
    }
  }
};

const loadNotificationsData = async () => {
  try {
    if (store.getters['auth/isLoggedIn']) {
      await Promise.allSettled([
        store.dispatch('notifications/getTheCountOfUnreads'),
        store.dispatch('notifications/getNotifications'),
        store.dispatch('user/getCurrentUserInfo')
      ]);
    }
  } catch (error) {
    console.error('Failed to load notifications:', error);
  }
}

// Watch for auth state changes
watch(
  () => store.getters['auth/isLoggedIn'],
  (isLoggedIn) => {
    if (isLoggedIn) {
      loadNotificationsData();
    }
  }, {
    immediate: true
  }
);

onUnmounted(() => {
  clearToasts();
});
</script>

<style lang="scss">
.main-app {
  position: relative;
  display: flex;
  background: white;
}

.left-app {
  .side-bar-out {
    display: none;
  }
}

.right-app {
  width: calc(100% - 100px);
  z-index: -1; // !!! WTF => check and refact
  position: absolute; // !!! WTF => check and refact
  right: 0;
  background: white;
  transition: 0.4s;
  display: flex;
  flex-direction: column;
  height: 100vh;

  .top-right {
    margin-bottom: 50px;
  }

  .bottom-right {
    margin-top: auto;
  }
}

@media screen and (max-width: 950px) {
  .right-app .bottom-right {
    margin-top: 0px;
  }
}

.right-app.center-app {
  width: 100%;
  top: 30px;
}

@media screen and (max-width: 640px) {
  .nav-close {
    .right-app {
      width: 100%;

      transition: 0.4s;
    }
  }

  .left-app {
    .side-bar-out {
      width: 37px;
      height: 37px;

      position: fixed;
      bottom: 24px;
      right: calc((67px / 2) - (37px / 2));
      z-index: 2;

      flex: 0 0 37px;
      display: flex;
      align-items: center;
      justify-content: center;

      border: none;
      border-radius: 4px;
      background-color: rgba(black, 0.9);

      svg {
        width: 6px;
        height: 12px;
        transform: rotate(180deg);
      }
    }
  }

  .right-app {
    width: calc(100% - 71px);

    right: unset;
    left: 0;
  }
}

//Animation
.fade-enter-active,
.fade-leave-active {
  // transition: all 0.5s ease;
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-enter-from,
.fade-leave-to {
  // transition: all 0.5s ease;
  opacity: 0;
}

// scale-up-to-up
.scale-up-to-up-enter-active {
  animation: scaleUp 0.6s ease both;
  animation-delay: 0.1s;
}

.scale-up-to-up-leave-active {
  animation: scaleDownUp 0.5s ease both;
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.8);
    // transform: translateY(-30px);
  }
}

@keyframes scaleDownUp {
  to {
    opacity: 0;
    transform: scale(0.6);
    //   transform: translateY(-30px);
  }
}
</style>
